import { useEffect, useState } from "react";
import { api, fmtDate, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Trash, MagnifyingGlass, Upload, DownloadSimple } from "@phosphor-icons/react";
import { Field } from "./Employees";
import ActionsMenu from "@/components/ActionsMenu";

const newContact = () => ({ full_name: "", designation: "", email: "", phone1: "", phone2: "" });

const EMPTY = {
  company_name: "", industry_type: "", website: "", notes: "",
  gstin: "", pan: "", group: "",
  opening_balance: 0, credit_limit: 0, discount: "", default_due_date: "", net_balance: 0,
  billing: { address1: "", address2: "", city: "", state: "", pincode: "", country: "India" },
  shipping: { title: "", address1: "", address2: "", city: "", state: "", pincode: "" },
  contact_persons: [newContact()], shared_with: [],
};

async function downloadFile(url, filename) {
  const tok = localStorage.getItem("smipl_token");
  const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
  if (!res.ok) { toast.error("Download failed"); return; }
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = filename; a.click();
  URL.revokeObjectURL(a.href);
}

export default function Accounts() {
  const { user } = useAuth();
  const canDelete = user?.role === "super_admin";
  const canManage = ["super_admin", "hr"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);

  const load = () => api.get("/accounts", { params: { q: q || undefined } }).then((r) => { setItems(r.data); setSelected(new Set()); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [q]);

  const save = async () => {
    if (!form.company_name) return toast.error("Company name required");
    const contacts = (form.contact_persons || []).filter((c) => c.full_name && c.phone1);
    const payload = { ...form, contact_persons: contacts.length ? contacts : [newContact()] };
    try {
      if (editing) await api.put(`/accounts/${editing.id}`, payload);
      else await api.post("/accounts", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeOne = async (a) => {
    if (!window.confirm(`Delete customer "${a.company_name}"? This action cannot be undone.`)) return;
    try { await api.delete(`/accounts/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addContact = () => setForm({ ...form, contact_persons: [...(form.contact_persons || []), newContact()] });
  const updContact = (i, k, v) => {
    const arr = [...form.contact_persons]; arr[i] = { ...arr[i], [k]: v }; setForm({ ...form, contact_persons: arr });
  };
  const rmContact = (i) => {
    const arr = [...form.contact_persons]; arr.splice(i, 1); setForm({ ...form, contact_persons: arr });
  };

  const toggleSel = (id) => {
    const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((a) => a.id)));
  };

  const bulkDelete = async () => {
    try {
      const r = await api.post("/accounts/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${r.data.deleted} customer(s)`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteAll = async () => {
    try {
      const r = await api.post("/accounts/delete-all");
      toast.success(`Deleted all ${r.data.deleted} customer(s)`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="accounts-page">
      <PageHeader
        title="Customers"
        description="Customer companies with billing, shipping and contacts."
        action={
          <div className="flex items-center gap-2">
            <ActionsMenu
              entityLabel="Customers"
              selectedCount={selected.size}
              canDestructive={canDelete}
              canManage={canManage}
              onImport={() => setShowImport(true)}
              onDownloadExcel={() => downloadFile(`${API_BASE}/accounts/export/excel`, "customers.xlsx")}
              onDownloadPdf={() => downloadFile(`${API_BASE}/accounts/export/pdf`, "customers.pdf")}
              onDownloadCsv={() => downloadFile(`${API_BASE}/accounts/export/csv`, "customers.csv")}
              onBulkEdit={() => setShowBulkEdit(true)}
              onBulkDelete={bulkDelete}
              onDeleteAll={deleteAll}
            />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditing(null); setForm(EMPTY); }} className="btn-brand" data-testid="account-new">
                  <Plus size={16} className="mr-1.5" />New Customer
                </Button>
              </DialogTrigger>
              <CustomerFormDialog form={form} setForm={setForm} editing={editing} save={save} setOpen={setOpen}
                addContact={addContact} updContact={updContact} rmContact={rmContact} />
            </Dialog>
          </div>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white dark:bg-slate-800 border rounded-md px-3 py-1.5">
          <MagnifyingGlass size={16} className="opacity-40" />
          <input data-testid="accounts-search" placeholder="Search company, GSTIN, city…" value={q} onChange={(e) => setQ(e.target.value)}
                 className="bg-transparent outline-none text-sm w-full" />
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {canDelete && (
                <TableHead className="w-10">
                  <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]"
                         checked={selected.size === items.length && items.length > 0}
                         onChange={toggleAll} data-testid="accounts-select-all" />
                </TableHead>
              )}
              <TableHead>Company</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Primary Contact</TableHead>
              <TableHead>Created</TableHead>
              {canDelete && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((a) => {
              const bill = a.billing || {};
              const cp = (a.contact_persons || [])[0] || {};
              return (
                <TableRow
                  key={a.id}
                  className="cursor-pointer"
                  onClick={(e) => {
                    if (e.target.closest("input,[data-stop]")) return;
                    setEditing(a);
                    setForm({ ...EMPTY, ...a, billing: { ...EMPTY.billing, ...(a.billing || {}) }, shipping: { ...EMPTY.shipping, ...(a.shipping || {}) }, contact_persons: a.contact_persons?.length ? a.contact_persons : [newContact()] });
                    setOpen(true);
                  }}
                >
                  {canDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="h-4 w-4 accent-[#fd7e14]"
                             checked={selected.has(a.id)} onChange={() => toggleSel(a.id)}
                             data-testid={`account-check-${a.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{a.company_name}</TableCell>
                  <TableCell className="text-xs font-mono">{a.gstin || "—"}</TableCell>
                  <TableCell className="text-sm">{[bill.city || a.city, bill.state || a.state, bill.country || a.country].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell className="text-sm">{cp.full_name || "—"}<div className="text-xs opacity-60 font-mono">{cp.phone1 || ""}</div></TableCell>
                  <TableCell className="text-xs opacity-60">{fmtDate(a.created_at)}</TableCell>
                  {canDelete && (
                    <TableCell className="text-right" data-stop onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" className="text-red-600 dark:text-red-400" onClick={() => removeOne(a)} data-testid={`account-delete-${a.id}`}>Delete</Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={canDelete ? 7 : 6} className="text-center text-sm opacity-60 py-10">No customers yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <ImportCustomersDialog open={showImport} onOpenChange={setShowImport} onDone={load} />
      <BulkEditCustomersDialog open={showBulkEdit} onOpenChange={setShowBulkEdit} ids={Array.from(selected)} onDone={load} />
    </div>
  );
}

function CustomerFormDialog({ form, setForm, editing, save, setOpen, addContact, updContact, rmContact }) {
  const b = form.billing || {}; const s = form.shipping || {};
  const setB = (k, v) => setForm({ ...form, billing: { ...b, [k]: v } });
  const setS = (k, v) => setForm({ ...form, shipping: { ...s, [k]: v } });
  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
      <div className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2"><Field label="Company Name *"><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="customer-company" /></Field></div>
          <div>
            <Field label="GSTIN">
              <div className="flex gap-2">
                <Input value={form.gstin || ""} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="customer-gstin" />
                <Button type="button" variant="outline" size="sm" data-testid="gstin-fetch"
                  onClick={async () => {
                    const g = (form.gstin || "").trim();
                    if (!g) return toast.error("Enter a GSTIN first");
                    try {
                      const r = await api.get(`/gstin/lookup/${g}`);
                      const next = { ...form };
                      if (r.data.legal_name && !next.company_name) next.company_name = r.data.legal_name;
                      if (r.data.pan && !next.pan) next.pan = r.data.pan;
                      const b = { ...(next.billing || {}) };
                      if (r.data.address && !b.address1) b.address1 = r.data.address;
                      if (r.data.state && !b.state) b.state = r.data.state;
                      if (r.data.pincode && !b.pincode) b.pincode = r.data.pincode;
                      next.billing = b;
                      setForm(next);
                      toast.success(r.data.online
                        ? "Fetched from GST portal"
                        : `GSTIN valid · State ${r.data.state} · PAN autofilled. Public lookup unavailable — fill the rest manually.`);
                    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid GSTIN"); }
                  }}
                >Fetch Details</Button>
              </div>
            </Field>
          </div>
          <Field label="PAN"><Input value={form.pan || ""} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} /></Field>
          <Field label="Group / Industry"><Input value={form.group || form.industry_type || ""} onChange={(e) => setForm({ ...form, group: e.target.value, industry_type: e.target.value })} /></Field>
          <Field label="Website"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
        </div>

        <div>
          <h4 className="font-semibold text-sm mb-2">Billing Address</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Field label="Address Line 1"><Input value={b.address1 || ""} onChange={(e) => setB("address1", e.target.value)} /></Field></div>
            <div className="sm:col-span-2"><Field label="Address Line 2"><Input value={b.address2 || ""} onChange={(e) => setB("address2", e.target.value)} /></Field></div>
            <Field label="City"><Input value={b.city || ""} onChange={(e) => setB("city", e.target.value)} /></Field>
            <Field label="State"><Input value={b.state || ""} onChange={(e) => setB("state", e.target.value)} /></Field>
            <Field label="Pincode"><Input value={b.pincode || ""} onChange={(e) => setB("pincode", e.target.value)} /></Field>
            <Field label="Country"><Input value={b.country || "India"} onChange={(e) => setB("country", e.target.value)} /></Field>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-sm mb-2">Shipping Address</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Field label="Shipping Title"><Input value={s.title || ""} onChange={(e) => setS("title", e.target.value)} /></Field></div>
            <div className="sm:col-span-2"><Field label="Address Line 1"><Input value={s.address1 || ""} onChange={(e) => setS("address1", e.target.value)} /></Field></div>
            <div className="sm:col-span-2"><Field label="Address Line 2"><Input value={s.address2 || ""} onChange={(e) => setS("address2", e.target.value)} /></Field></div>
            <Field label="City"><Input value={s.city || ""} onChange={(e) => setS("city", e.target.value)} /></Field>
            <Field label="State"><Input value={s.state || ""} onChange={(e) => setS("state", e.target.value)} /></Field>
            <Field label="Pincode"><Input value={s.pincode || ""} onChange={(e) => setS("pincode", e.target.value)} /></Field>
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-sm mb-2">Financials</h4>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Opening Balance"><Input type="number" value={form.opening_balance || 0} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} /></Field>
            <Field label="Credit Limit"><Input type="number" value={form.credit_limit || 0} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} /></Field>
            <Field label="Default Due Date"><Input value={form.default_due_date || ""} onChange={(e) => setForm({ ...form, default_due_date: e.target.value })} placeholder="e.g. Net 30" /></Field>
            <Field label="Discount"><Input value={form.discount || ""} onChange={(e) => setForm({ ...form, discount: e.target.value })} /></Field>
            <Field label="Net Balance"><Input type="number" value={form.net_balance || 0} onChange={(e) => setForm({ ...form, net_balance: Number(e.target.value) })} /></Field>
          </div>
        </div>

        <div><Field label="Notes"><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field></div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm">Contact Persons</h4>
            <Button size="sm" variant="outline" onClick={addContact}><Plus size={14} className="mr-1" />Add</Button>
          </div>
          <div className="space-y-3">
            {(form.contact_persons || []).map((c, i) => (
              <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 grid sm:grid-cols-5 gap-2 items-start">
                <Input placeholder="Full Name" value={c.full_name} onChange={(e) => updContact(i, "full_name", e.target.value)} />
                <Input placeholder="Designation" value={c.designation || ""} onChange={(e) => updContact(i, "designation", e.target.value)} />
                <Input placeholder="Email" value={c.email || ""} onChange={(e) => updContact(i, "email", e.target.value)} />
                <Input placeholder="Phone 1" value={c.phone1} onChange={(e) => updContact(i, "phone1", e.target.value)} />
                <div className="flex gap-1">
                  <Input placeholder="Phone 2" value={c.phone2 || ""} onChange={(e) => updContact(i, "phone2", e.target.value)} />
                  {(form.contact_persons || []).length > 1 && (
                    <Button size="icon" variant="ghost" onClick={() => rmContact(i)}><Trash size={14} className="text-red-600" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={save} className="btn-brand" data-testid="account-save">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ImportCustomersDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTpl = async () => {
    await downloadFile(`${API_BASE}/accounts/import/template`, "customers_import_template.xlsx");
  };
  const submit = async () => {
    if (!file) return toast.error("Pick an Excel file first");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/accounts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} customer(s)${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
      onOpenChange(false); setFile(null); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Import Customers from Excel</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="font-medium mb-1">Step 1 — Download the template</div>
            <p className="opacity-70 text-xs mb-2">Includes all 26 columns: Name, Phone, Email, GSTIN, Company, Billing &amp; Shipping Address, Opening Balance, PAN, etc.</p>
            <Button size="sm" variant="outline" onClick={downloadTpl} data-testid="customer-tpl-dl"><DownloadSimple size={14} className="mr-1.5" />Download Template</Button>
          </div>
          <div>
            <div className="font-medium mb-1">Step 2 — Upload your filled file</div>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="customer-import-file" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} className="btn-brand" data-testid="customer-import-submit">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkEditCustomersDialog({ open, onOpenChange, ids, onDone }) {
  const [upd, setUpd] = useState({ group: "", city: "", state: "", country: "" });
  const submit = async () => {
    const payload = Object.fromEntries(Object.entries(upd).filter(([, v]) => v));
    if (!Object.keys(payload).length) return toast.error("Fill at least one field to update");
    try {
      const r = await api.post("/accounts/bulk-update", { ids, updates: payload });
      toast.success(`Updated ${r.data.updated} customer(s)`); onOpenChange(false); setUpd({ group: "", city: "", state: "", country: "" }); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Bulk edit {ids.length} customer(s)</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="opacity-70">Only non-empty fields below will be applied. Rest stay untouched.</p>
          <Field label="Group / Industry"><Input value={upd.group} onChange={(e) => setUpd({ ...upd, group: e.target.value })} /></Field>
          <Field label="City"><Input value={upd.city} onChange={(e) => setUpd({ ...upd, city: e.target.value })} /></Field>
          <Field label="State"><Input value={upd.state} onChange={(e) => setUpd({ ...upd, state: e.target.value })} /></Field>
          <Field label="Country"><Input value={upd.country} onChange={(e) => setUpd({ ...upd, country: e.target.value })} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="btn-brand" data-testid="customer-bulk-edit-submit">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
